// Minimal history router. Routes are matched in order; ':name' segments become
// params and a trailing '*' matches the rest. Links are plain <a href> elements:
// a document-level click handler turns same-origin navigations into pushState.

import type { Component } from 'svelte';

export interface RouteDef {
  path: string;
  /** Lazily loaded page component. */
  load: () => Promise<{ default: Component<any> }>;
  /** 'app' = signed-in shell, 'public' = auth / legal pages. */
  layout: 'app' | 'public';
  /** i18n key for the document title. */
  title?: string;
}

interface Match {
  route: RouteDef;
  params: Record<string, string>;
}

function compile(path: string): RegExp {
  const pattern = path
    .replace(/\/$/, '')
    .split('/')
    .map((seg) => {
      if (seg === '*') return '(?:/(?<rest>.*))?';
      if (seg.startsWith(':')) return `/(?<${seg.slice(1)}>[^/]+)`;
      return seg ? `/${seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}` : '';
    })
    .join('');
  return new RegExp(`^${pattern || ''}/?$`);
}

class Router {
  path = $state(typeof location !== 'undefined' ? location.pathname : '/');
  search = $state(typeof location !== 'undefined' ? location.search : '');
  private routes: Array<{ def: RouteDef; re: RegExp }> = [];
  private guards: Array<(to: string) => string | null> = [];

  query = $derived(new URLSearchParams(this.search));

  setRoutes(routes: RouteDef[]) {
    this.routes = routes.map((def) => ({ def, re: compile(def.path) }));
  }

  /** A guard may return a different path to redirect to. */
  addGuard(fn: (to: string) => string | null) {
    this.guards.push(fn);
  }

  match(path = this.path): Match | null {
    for (const { def, re } of this.routes) {
      const m = re.exec(path);
      if (m) return { route: def, params: { ...(m.groups || {}) } };
    }
    return null;
  }

  navigate(to: string, opts: { replace?: boolean; keepScroll?: boolean } = {}) {
    const url = new URL(to, location.origin);
    let target = url.pathname + url.search + url.hash;
    for (const guard of this.guards) {
      const redirect = guard(url.pathname);
      if (redirect && redirect !== url.pathname) {
        target = redirect;
        break;
      }
    }
    const current = location.pathname + location.search + location.hash;
    if (target === current) return;
    if (opts.replace) history.replaceState({}, '', target);
    else history.pushState({}, '', target);
    this.sync();
    if (!opts.keepScroll) window.scrollTo({ top: 0 });
  }

  /** Update only the query string (filters), without a new history entry by default. */
  setQuery(values: Record<string, string | number | null | undefined>, opts: { push?: boolean } = {}) {
    const params = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(values)) {
      if (value === null || value === undefined || value === '') params.delete(key);
      else params.set(key, String(value));
    }
    const qs = params.toString();
    const target = location.pathname + (qs ? `?${qs}` : '');
    if (opts.push) history.pushState({}, '', target);
    else history.replaceState({}, '', target);
    this.search = location.search;
  }

  sync() {
    this.path = location.pathname;
    this.search = location.search;
  }

  start() {
    window.addEventListener('popstate', () => this.sync());
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download') || anchor.dataset.native !== undefined) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) return;
      // Server-rendered pages stay full page loads.
      if (/^\/(logout|verify-email|unsubscribe|ticket-reply|admin|api)(\/|$)/.test(url.pathname)) return;
      event.preventDefault();
      this.navigate(url.pathname + url.search + url.hash);
    });
  }

  isActive(prefix: string, exact = false): boolean {
    return exact ? this.path === prefix : this.path === prefix || this.path.startsWith(prefix + '/');
  }
}

export const router = new Router();
export const navigate = (to: string, opts?: { replace?: boolean; keepScroll?: boolean }) => router.navigate(to, opts);
